import React, { FC, useEffect, useState } from 'react';
import { SupplierValidationRecord } from '@mortee/domain/validationSystem';
import EditValidationRecordMainTabs, {
  MainTabs,
} from '@mortee/routes/validationSystem/editValidtionRecord/mainTabs/EditValidationRecordMainTabs';
import EditValidationRecordSecondaryTabs, {
  SecondaryTabs,
} from '@mortee/routes/validationSystem/editValidtionRecord/secondaryTabs/EditValidationRecordSecondaryTabs';
import styled from '@emotion/styled';
import { SelectedTab } from '@mortee/components/ProgressTabs';
import SeparatorLine from '@app/components/SeparatorLine';
import useLoadable from '@app/hooks/loadable/useLoadable';
import validationSystemServices from '@mortee/services/validationSystemServices';
import { AlertStatus, transformValidationAlert } from '@mortee/domain/validationSystemAlerts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Menu } from 'lucide-react';
import { readRightDrawerCollapsed, writeRightDrawerCollapsed } from '@app/stores/validationRecordDrawerCache';

interface Props {
  record: SupplierValidationRecord;
  className?: string;
  selectedMainTab: SelectedTab<MainTabs>;
  selectedSecondaryTab: SelectedTab<SecondaryTabs>;

  onSaved(updatedRecord: SupplierValidationRecord | undefined, userWantsToClose: boolean): void;

  onIsFormUnsavedChanged(isFormUnsaved: boolean | null): void;

  onRecordRefreshRequested?(): void;
}

export const HasAlertsContext = React.createContext<boolean>(false);

const EditValidationRecordForm: FC<Props> = (props) => {
  const { record, selectedMainTab, selectedSecondaryTab, onSaved, onIsFormUnsavedChanged, className, onRecordRefreshRequested } =
    props;
  // Seed from localStorage so the right drawer (comments & alerts) keeps its collapsed/expanded
  // state across records and reloads; persist on every change.
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState<boolean>(() => readRightDrawerCollapsed());

  useEffect(() => {
    writeRightDrawerCollapsed(isRightPanelCollapsed);
  }, [isRightPanelCollapsed]);

  const [timelineAlerts, reloadAlerts] = useLoadable(async () => {
    const serverResponse = await validationSystemServices.getAlertsByStaticId(record.staticId);
    return serverResponse.map((response) => transformValidationAlert(response));
  }, [record.versionId]);

  function hasActiveAlertsOrLoadingAlerts(): boolean {
    if (timelineAlerts.isResolved()) {
      return timelineAlerts.result?.some((alert) => alert.alertStatus === AlertStatus.active);
    }
    return true;
  }

  return (
    <MainLayout isCollapsed={isRightPanelCollapsed} className={className}>
      <HasAlertsContext.Provider value={hasActiveAlertsOrLoadingAlerts()}>
        <EditValidationRecordMainTabs
          selectedTab={selectedMainTab}
          record={record}
          onIsFormUnsavedChanged={onIsFormUnsavedChanged}
          onSaved={onSaved}
          onRecordRefreshRequested={onRecordRefreshRequested}
        />
      </HasAlertsContext.Provider>
      <SeparatorContainer>
        {!isRightPanelCollapsed && (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToggleButton
                    onClick={(): void => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
                    aria-label='Collapse right panel'
                    isCollapsed={isRightPanelCollapsed}
                  >
                    →
                  </ToggleButton>
                </TooltipTrigger>
                <TooltipContent side='left'>
                  <p>Collapse right panel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <SeparatorLine vertical />
          </>
        )}
      </SeparatorContainer>
      <RightPanel data-testid='edit-validation-record-right-panel' isCollapsed={isRightPanelCollapsed}>
        {isRightPanelCollapsed ? (
          <CollapsedPanelContent onClick={(): void => setIsRightPanelCollapsed(false)}>
            <Menu size={24} />
          </CollapsedPanelContent>
        ) : (
          <EditValidationRecordSecondaryTabs
            selectedTab={selectedSecondaryTab}
            selectedMainTab={selectedMainTab.value}
            record={record}
            allAlertsLoadable={timelineAlerts}
            reloadAlerts={reloadAlerts}
          />
        )}
      </RightPanel>
    </MainLayout>
  );
};

export default EditValidationRecordForm;

const MainLayout = styled.div<{ isCollapsed: boolean }>`
  display: grid;
  grid-template-columns: 3fr auto ${({ isCollapsed }): string => (isCollapsed ? '60px' : 'minmax(350px, 1fr)')};
  // Fill the bounded modal body and delegate scrolling to the columns, so the Verification tab's
  // middle band can scroll while its header/footer stay fixed (instead of the whole grid scrolling).
  min-height: 0;
  overflow: hidden;
  transition: grid-template-columns 0.3s ease;
`;

const SeparatorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const RightPanel = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  // Bound to the modal height so the comment list scrolls internally and the comment input stays
  // pinned at the bottom (it is already the last, non-scrolling child of the comments panel).
  min-height: 0;
  transition: all 0.3s ease;
  background-color: ${({ isCollapsed }): string => (isCollapsed ? '#f5f5f5' : 'transparent')};
  align-items: ${({ isCollapsed }): string => (isCollapsed ? 'center' : 'stretch')};
  justify-content: ${({ isCollapsed }): string => (isCollapsed ? 'flex-start' : 'flex-start')};
  border-left: 1px solid #e0e0e0;
`;

const CollapsedPanelContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 20px;
  color: #666;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const ToggleButton = styled.button<{ isCollapsed: boolean }>`
  position: absolute;
  top: 20px;
  right: ${({ isCollapsed }): string => (isCollapsed ? '10px' : '-20px')};
  z-index: 10;
  width: 40px;
  height: 40px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    background: #e0e0e0;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }
`;
